import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Package, ArrowRight, ChevronRight } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { categoryService, productService } from '../services/api';

// Product image component with fallback
const CategoryImage = ({ src, alt, className }) => {
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  const getColorFromName = (name) => {
    const colors = [
      'from-pink-400 to-pink-600',
      'from-blue-400 to-blue-600',
      'from-green-400 to-green-600',
      'from-purple-400 to-purple-600',
      'from-orange-400 to-orange-600',
      'from-teal-400 to-teal-600',
    ];
    const hash = (name || '').split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  if (hasError || !src) {
    return (
      <div className={`${className} bg-gradient-to-br ${getColorFromName(alt)} flex items-center justify-center`}>
        <div className="text-center text-white p-4">
          <Package className="h-10 w-10 mx-auto mb-2 opacity-80" />
          <p className="text-sm font-medium opacity-90">{alt}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${className} relative bg-white`}>
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
          <div className="animate-pulse text-gray-400">
            <Package className="h-6 w-6" />
          </div>
        </div>
      )}
      <img
        src={src}
        alt={alt}
        loading="lazy"
        className={`w-full h-full object-contain p-4 ${isLoading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}
        onError={() => setHasError(true)}
        onLoad={() => setIsLoading(false)}
      />
    </div>
  );
};

const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        const categoriesData = await categoryService.getAllCategories();
        
        // Fetch sample images for each category
        const categoryImagePromises = categoriesData.map(cat => 
          productService.getAllProducts(cat.category_id, null, 1)
        );
        const categoryProductsResults = await Promise.all(categoryImagePromises);
        
        const categoriesWithImages = categoriesData.map((cat, index) => {
          const products = categoryProductsResults[index];
          const sampleProduct = products.find(p => p.local_image) || products[0];
          const imageUrl = sampleProduct?.local_image 
            ? `https://procloudpromo.com${sampleProduct.local_image}`
            : sampleProduct?.image_url || '';
          return {
            ...cat,
            image: imageUrl
          };
        });
        
        setCategories(categoriesWithImages);
      } catch (error) {
        console.error('Error loading categories:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600 mx-auto mb-4"></div>
          <p className="text-lg text-gray-600">Loading categories...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-pink-900 to-pink-700 text-white py-12">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl font-bold mb-2">All Categories</h1>
          <p className="text-pink-100">
            Browse our complete range of {categories.length} product categories
          </p>
        </div>
      </div>

      {/* Categories Grid */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {categories.map((category) => (
            <Link 
              key={category.category_id} 
              to={`/products/${category.category_id}`}
              data-testid={`category-card-${category.category_id}`}
            >
              <Card className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden h-full">
                <CardContent className="p-0">
                  <div className="relative aspect-square bg-gray-100">
                    <CategoryImage
                      src={category.image}
                      alt={category.name}
                      className="w-full h-full"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                      <h3 className="font-bold text-lg mb-1 group-hover:text-pink-300 transition-colors flex items-center justify-between">
                        {category.name}
                        <ChevronRight className="h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </h3>
                      <p className="text-sm text-white/90">
                        {category.product_count} products
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        {/* CTA Section */}
        <div className="text-center mt-12 p-8 bg-white rounded-2xl shadow-lg">
          <h2 className="text-2xl font-bold text-gray-900 mb-3">
            Can't find what you're looking for?
          </h2>
          <p className="text-gray-600 mb-6 max-w-xl mx-auto">
            Our team can help you find the perfect promotional products for your needs. 
            Contact us for personalized recommendations.
          </p>
          <Link 
            to="/contact"
            className="inline-flex items-center gap-2 bg-pink-600 text-white px-6 py-3 rounded-full font-semibold hover:bg-pink-700 transition-colors"
          >
            Contact Us
            <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Categories;
